/**
 * Chiamate alle API dei progetti e degli accessi.
 *
 * Provvisorio dichiarato: questi tipi sono scritti a mano, mentre CLAUDE.md
 * §2.3 impone di generarli dagli schemi OpenAPI dei servizi. La generazione è
 * tracciata come voce aperta nel file di sessione; finché non c'è, ogni
 * modifica a uno schema del backend va riflessa qui a mano.
 */

import type { ApiClient } from './client';
import type { PlanId, UserPlan } from './plans';

/** Stato del ciclo di vita di un progetto. */
export type ProjectStatus = 'active' | 'suspended' | 'archived';

/** Natura di una dipendenza dichiarata fra due progetti. */
export type LinkKind = 'uses_api' | 'shares_data';

/** Azione richiedibile su una risorsa. */
export type Action = 'read' | 'write' | 'admin';

/** Un progetto. */
export interface Project {
  /** Identificativo del progetto. */
  id: string;
  /** Identificativo leggibile e stabile. */
  slug: string;
  /** Nome visualizzato. */
  name: string;
  /** Descrizione libera. */
  description: string;
  /** Stato del ciclo di vita. */
  status: ProjectStatus;
  /** Etichetta del principal che lo ha creato. */
  created_by: string;
  /** Istante di creazione, ISO 8601. */
  created_at: string;
  /** Istante di archiviazione, se archiviato. */
  archived_at: string | null;
  /** Repository git https del progetto; stringa vuota se non impostato. */
  repository_url: string;
  /**
   * Nome leggibile di chi lo ha creato (ADR 0048 §8); vuoto o assente se la piattaforma
   * non lo conosce (progetti nati prima del 2026-10-22, backend di prima).
   */
  created_by_name?: string;
  /**
   * Sottodominio del sito (ADR 0054): `<sottodominio>.institorai.app` in produzione e
   * `<sottodominio>-demo.institorai.app` in demo. `null` o assente per i progetti nati prima.
   */
  subdomain?: string | null;
}

/** Motivo per cui un sottodominio non è disponibile (ADR 0054 §4). */
export type SubdomainUnavailableReason = 'taken' | 'reserved' | 'invalid';

/** Un sito che ha cambiato indirizzo (ADR 0054 §10). */
export interface RenamedSite {
  /** `demo` o `prod`. */
  environment: 'demo' | 'prod';
  /** Nome nuovo, senza schema. */
  hostname: string;
  /** Nome di prima, che rimanda al nuovo. */
  previous_hostname: string;
}

/** Risposta di `PUT /api/v1/projects/{id}/subdomain`. */
export interface SubdomainChange {
  /** Progetto con il sottodominio nuovo. */
  project: Project;
  /** Siti rinominati; vuoto se il progetto non ne ha ancora. */
  sites: RenamedSite[];
}

/** Risposta di `GET /api/v1/projects/subdomains/{nome}`. */
export interface SubdomainAvailability {
  /** Sottodominio normalizzato dal server. */
  subdomain: string;
  /** True se nessun progetto o sito lo usa e non è riservato. */
  available: boolean;
  /** Motivo dell'indisponibilità, `null` se disponibile. */
  reason: SubdomainUnavailableReason | null;
  /** Nome libero proposto dal server con `taken` o `reserved`, altrimenti `null`. */
  suggestion: string | null;
  /** Indirizzo di produzione risultante; vuoto con `invalid`. */
  prod_host: string;
  /** Indirizzo della demo risultante; vuoto con `invalid`. */
  demo_host: string;
}

/** Una dipendenza dichiarata fra due progetti. */
export interface ProjectLink {
  /** Identificativo del collegamento. */
  id: string;
  /** Progetto che dichiara la dipendenza. */
  source_project_id: string;
  /** Progetto da cui si dipende. */
  target_project_id: string;
  /** Natura della dipendenza. */
  kind: LinkKind;
  /** Quali API e per quale scopo. */
  notes: string;
  /** Etichetta di chi ha dichiarato il collegamento. */
  created_by: string;
  /** Istante della dichiarazione, ISO 8601. */
  created_at: string;
}

/** Un permesso concesso su una risorsa. */
export interface Grant {
  /** Identificativo del grant. */
  id: string;
  /** Principal beneficiario. */
  principal_id: string;
  /** Tipo della risorsa. */
  resource_type: string;
  /** Identificativo della risorsa. */
  resource_id: string;
  /** Azione concessa. */
  action: Action;
  /** Etichetta di chi ha concesso. */
  granted_by: string;
  /** Istante della concessione, ISO 8601. */
  granted_at: string;
  /** Scadenza, se impostata. */
  expires_at: string | null;
  /** Istante della revoca, se revocato. */
  revoked_at: string | null;
  /** Motivazione della concessione. */
  reason: string;
}

/** Un principal registrato: utente o agente. */
export interface PrincipalSummary {
  /** Identificativo interno. */
  id: string;
  /** `user` o `agent`. */
  type: 'user' | 'agent';
  /** Identificativo esterno. */
  subject_id: string;
  /** Nome leggibile. */
  display_name: string;
  /** Progetto di appartenenza, per gli agenti. */
  home_project_id: string | null;
  /** False se sospeso. */
  is_active: boolean;
}

/** Un utente della directory. */
export interface DirectoryUser {
  /** Claim `sub` di Keycloak. */
  subject_id: string;
  /** Nome utente nel realm. */
  username: string;
  /** Indirizzo email. */
  email: string;
  /** Nome leggibile. */
  display_name: string;
  /** False se l'account è disabilitato nel realm. */
  enabled: boolean;
  /** Ruoli di piattaforma. */
  roles: string[];
  /** Piano dell'utente (ADR 0023); `free` per chi non ha un'assegnazione. */
  plan: PlanId;
}

/** Dati per la creazione di un progetto. */
export interface NewProject {
  /** Identificativo leggibile proposto. */
  slug: string;
  /** Nome visualizzato. */
  name: string;
  /** Descrizione libera. */
  description: string;
  /** Repository git https, facoltativo. */
  repository_url?: string;
  /** Sottodominio del sito (ADR 0054); la creazione lo prenota. */
  subdomain?: string;
}

/** Campi modificabili di un progetto; quelli omessi restano com'erano. */
export interface ProjectChanges {
  /** Nuovo nome. */
  name?: string;
  /** Nuova descrizione. */
  description?: string;
  /** Nuovo repository; stringa vuota lo toglie. Richiede l'amministrazione. */
  repository_url?: string;
}

/** Dati per la concessione di un permesso. */
export interface NewGrant {
  /** Principal beneficiario. */
  principal_id: string;
  /** Progetto oggetto del grant. */
  resource_id: string;
  /** Azione da concedere. */
  action: Action;
  /** Motivazione, obbligatoria. */
  reason: string;
}

/** Operazioni sui progetti e sugli accessi, esposte alla UI. */
export class ProjectsApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Elenca i progetti visibili all'utente autenticato.
   *
   * Il filtro lo applica il backend: la UI non nasconde nulla per conto proprio,
   * così ciò che si vede corrisponde sempre a ciò a cui si ha davvero accesso.
   *
   * @returns I progetti accessibili.
   */
  listProjects(): Promise<Project[]> {
    return this.client.request<Project[]>('GET', '/api/v1/projects');
  }

  /**
   * Crea un progetto.
   *
   * @param project Dati del nuovo progetto.
   * @returns Il progetto creato.
   */
  createProject(project: NewProject): Promise<Project> {
    return this.client.request<Project>('POST', '/api/v1/projects', project);
  }

  /**
   * Controlla se un sottodominio è libero (ADR 0054 §4).
   *
   * È solo un aiuto mentre l'utente scrive: decide la creazione, che risponde
   * 409 `subdomain_taken` se nel frattempo qualcuno lo ha preso.
   *
   * @param name Sottodominio da controllare.
   * @returns Disponibilità, motivo, suggerimento e indirizzi risultanti.
   */
  checkSubdomain(name: string, projectId?: string): Promise<SubdomainAvailability> {
    const query = projectId === undefined ? '' : `?project_id=${encodeURIComponent(projectId)}`;
    return this.client.request<SubdomainAvailability>(
      'GET',
      `/api/v1/projects/subdomains/${encodeURIComponent(name)}${query}`,
    );
  }

  /**
   * Cambia il sottodominio del sito di un progetto esistente (ADR 0054 §10).
   *
   * Demo e produzione prendono `<sottodominio>-demo` e `<sottodominio>`; gli indirizzi
   * di prima rimandano ai nuovi.
   *
   * @param projectId Progetto.
   * @param subdomain Sottodominio nuovo.
   * @returns Il progetto aggiornato e i siti che hanno cambiato indirizzo.
   * @throws ApiError 409 `subdomain_taken`, 422 `subdomain_invalid`, 403 senza
   *   amministrazione del progetto.
   */
  changeSubdomain(projectId: string, subdomain: string): Promise<SubdomainChange> {
    return this.client.request<SubdomainChange>('PUT', `/api/v1/projects/${projectId}/subdomain`, { subdomain });
  }

  /**
   * Modifica un progetto.
   *
   * @param projectId Progetto da modificare.
   * @param changes Campi da cambiare.
   * @returns Il progetto aggiornato.
   */
  updateProject(projectId: string, changes: ProjectChanges): Promise<Project> {
    return this.client.request<Project>('PATCH', `/api/v1/projects/${projectId}`, changes);
  }

  /**
   * Archivia un progetto.
   *
   * @param projectId Progetto da archiviare.
   * @returns Il progetto archiviato.
   */
  archiveProject(projectId: string): Promise<Project> {
    return this.client.request<Project>('POST', `/api/v1/projects/${projectId}/archive`);
  }

  /**
   * Cancella un progetto. Riservata agli `ADMIN` di piattaforma (ADR 0008 §10).
   *
   * Lo slug ripetuto è la conferma: il backend rifiuta se non coincide, così
   * un clic sbagliato non cancella il progetto sbagliato.
   *
   * @param projectId Progetto da cancellare.
   * @param confirmSlug Slug del progetto, digitato dall'utente.
   * @returns Nulla.
   */
  deleteProject(projectId: string, confirmSlug: string): Promise<void> {
    return this.client.request<void>('DELETE', `/api/v1/projects/${encodeURIComponent(projectId)}`, {
      confirm_slug: confirmSlug,
    });
  }

  /**
   * Elenca le dipendenze dichiarate che coinvolgono un progetto.
   *
   * @param projectId Progetto di riferimento.
   * @returns I collegamenti in entrata e in uscita.
   */
  listLinks(projectId: string): Promise<ProjectLink[]> {
    return this.client.request<ProjectLink[]>('GET', `/api/v1/projects/${projectId}/links`);
  }

  /**
   * Dichiara che un progetto dipende dalle API di un altro.
   *
   * Non concede alcun accesso agli agenti: quello richiede un grant esplicito.
   *
   * @param projectId Progetto sorgente.
   * @param targetProjectId Progetto da cui si dipende.
   * @param kind Natura della dipendenza.
   * @param notes Quali API e per quale scopo.
   * @returns Il collegamento creato.
   */
  createLink(
    projectId: string,
    targetProjectId: string,
    kind: LinkKind,
    notes: string,
  ): Promise<ProjectLink> {
    return this.client.request<ProjectLink>('POST', `/api/v1/projects/${projectId}/links`, {
      target_project_id: targetProjectId,
      kind,
      notes,
    });
  }

  /**
   * Rimuove una dipendenza dichiarata.
   *
   * @param linkId Collegamento da rimuovere.
   */
  deleteLink(linkId: string): Promise<ProjectLink> {
    return this.client.request<ProjectLink>('DELETE', `/api/v1/project-links/${linkId}`);
  }

  /**
   * Elenca chi ha accesso a un progetto.
   *
   * @param projectId Progetto da ispezionare.
   * @returns I grant attivi sul progetto.
   */
  listGrants(projectId: string): Promise<Grant[]> {
    // Prefisso `/api/v1/access`: il gateway instrada per prefisso, e una rotta
    // di `access` sotto `/api/v1/projects` finirebbe al servizio sbagliato.
    return this.client.request<Grant[]>('GET', `/api/v1/access/projects/${projectId}/grants`);
  }

  /**
   * Elenca gli agenti che appartengono a un progetto.
   *
   * @param projectId Progetto di appartenenza.
   * @returns Gli agenti del progetto.
   */
  listAgents(projectId: string): Promise<PrincipalSummary[]> {
    return this.client.request<PrincipalSummary[]>(
      'GET',
      `/api/v1/access/projects/${projectId}/agents`,
    );
  }

  /**
   * Restituisce il principal del chiamante.
   *
   * Serve alla dashboard per confrontare il proprio identificativo interno con
   * quelli mostrati negli elenchi di accesso.
   *
   * @returns Il principal dell'utente autenticato.
   */
  me(): Promise<PrincipalSummary> {
    return this.client.request<PrincipalSummary>('GET', '/api/v1/principals/me');
  }

  /**
   * Concede un permesso su un progetto.
   *
   * @param grant Dati della concessione.
   * @returns Il grant creato.
   */
  createGrant(grant: NewGrant): Promise<Grant> {
    return this.client.request<Grant>('POST', '/api/v1/grants', {
      ...grant,
      resource_type: 'project',
    });
  }

  /**
   * Revoca un permesso.
   *
   * @param grantId Grant da revocare.
   * @returns Il grant revocato.
   */
  revokeGrant(grantId: string): Promise<Grant> {
    return this.client.request<Grant>('DELETE', `/api/v1/grants/${grantId}`);
  }

  /**
   * Elenca gli utenti della piattaforma.
   *
   * Riservata agli amministratori: il backend risponde 403 agli altri, e la UI
   * gestisce il rifiuto invece di nascondere preventivamente la voce.
   *
   * @returns Gli utenti conosciuti.
   */
  listUsers(): Promise<DirectoryUser[]> {
    return this.client.request<DirectoryUser[]>('GET', '/api/v1/users');
  }

  /**
   * Legge il piano del chiamante.
   *
   * @returns Il piano con i limiti effettivi.
   */
  myPlan(): Promise<UserPlan> {
    return this.client.request<UserPlan>('GET', '/api/v1/me/plan');
  }

  /**
   * Legge il piano di un utente con lo storico dei cambi.
   *
   * Riservata agli amministratori: il backend risponde 403 agli altri.
   *
   * @param subjectId Utente.
   * @returns Piano e ultimi cambi.
   */
  userPlan(subjectId: string): Promise<UserPlan> {
    return this.client.request<UserPlan>(
      'GET',
      `/api/v1/users/${encodeURIComponent(subjectId)}/plan`,
    );
  }

  /**
   * Cambia il piano di un utente.
   *
   * Riservata agli amministratori della piattaforma.
   *
   * @param subjectId Utente.
   * @param plan Nuovo piano.
   * @param reason Motivo facoltativo, finisce nello storico.
   * @returns Piano e storico dopo il cambio.
   */
  changeUserPlan(subjectId: string, plan: PlanId, reason?: string): Promise<UserPlan> {
    const trimmed = reason?.trim() ?? '';
    return this.client.request<UserPlan>(
      'PUT',
      `/api/v1/users/${encodeURIComponent(subjectId)}/plan`,
      trimmed === '' ? { plan } : { plan, reason: trimmed },
    );
  }
}
